import { API_CONFIG } from '../../config/api';
import { formatDate } from '../../utils/dateFormatter';

export interface AssessmentRecord {
  id: string;
  createdAt: string;
  updatedAt?: string;
  [key: string]: any;
}

export interface MergedAssessmentData {
  latest: AssessmentRecord | null;
  history: AssessmentRecord[];
  isCompleted: boolean;
  lastUpdated: string | null;
}

/**
 * Assessment type configurations for handling different data structures
 */
const ASSESSMENT_TYPE_CONFIG = {
  spiritual: {
    historyEndpoint: API_CONFIG.ENDPOINTS.SPIRITUAL + '/history',
    latestEndpoint: API_CONFIG.ENDPOINTS.SPIRITUAL + '/latest',
    subTypes: ['fasdl', 'facit-sp-12'],
    mergeStrategy: 'combine_subtypes'
  },
  nutritional: {
    historyEndpoint: API_CONFIG.ENDPOINTS.NUTRITIONAL + '/history',
    latestEndpoint: API_CONFIG.ENDPOINTS.NUTRITIONAL + '/latest',
    mergeStrategy: 'standard'
  },
  physical: {
    historyEndpoint: API_CONFIG.ENDPOINTS.PHYSICAL_HEALTH + '/history',
    latestEndpoint: API_CONFIG.ENDPOINTS.PHYSICAL_HEALTH_LATEST,
    mergeStrategy: 'expand_categories'
  },
  'mental-health': {
    historyEndpoint: API_CONFIG.ENDPOINTS.EMOTIO_PSYCHO_RATIONAL + '/history',
    latestEndpoint: API_CONFIG.ENDPOINTS.EMOTIO_PSYCHO_RATIONAL + '/history?limit=20',
    subTypes: ['dass-21', 'phq-9', 'gad-7', 'gender'],
    mergeStrategy: 'by_category'
  },
  legendarial: {
    historyEndpoint: API_CONFIG.ENDPOINTS.LEGENDARIAL + '/history',
    latestEndpoint: API_CONFIG.ENDPOINTS.LEGENDARIAL + '/history',
    subTypes: ['swls', 'pil'],
    mergeStrategy: 'by_category'
  },
  'religio-traditio-cultural': {
    historyEndpoint: API_CONFIG.ENDPOINTS.RELIGIO_TRADITIO_CULTURAL + '/history',
    latestEndpoint: API_CONFIG.ENDPOINTS.RELIGIO_TRADITIO_CULTURAL + '/history',
    subTypes: ['brief-cope', 'cvscale', 'tvas'],
    mergeStrategy: 'by_category'
  },
  'socio-relational': {
    historyEndpoint: API_CONFIG.ENDPOINTS.SOCIO_RELATIONAL + '/history',
    latestEndpoint: API_CONFIG.ENDPOINTS.SOCIO_RELATIONAL_LATEST,
    mergeStrategy: 'standard'
  },
  financial: {
    historyEndpoint: API_CONFIG.ENDPOINTS.FINANCIAL + '/history',
    latestEndpoint: API_CONFIG.ENDPOINTS.FINANCIAL + '/history',
    subTypes: ['budgeting', 'savings', 'investment'],
    mergeStrategy: 'by_category'
  },
  environmental: {
    historyEndpoint: API_CONFIG.ENDPOINTS.ENVIRONMENTAL + '/history',
    latestEndpoint: API_CONFIG.ENDPOINTS.ENVIRONMENTAL + '/latest',
    mergeStrategy: 'standard'
  },
  occupational: {
    historyEndpoint: API_CONFIG.ENDPOINTS.OCCUPATIONAL + '/history',
    latestEndpoint: API_CONFIG.ENDPOINTS.OCCUPATIONAL + '/history',
    subTypes: ['cbi', 'uwes', 'employee-engagement'],
    mergeStrategy: 'by_category'
  }
} as const;

export type AssessmentType = keyof typeof ASSESSMENT_TYPE_CONFIG;

// Export the config for use in other modules
export { ASSESSMENT_TYPE_CONFIG };

/**
 * Determines if an assessment record is considered complete
 */
export function isAssessmentComplete(assessment: AssessmentRecord | null): boolean {
  if (!assessment) return false;

  // Check for nested assessmentData structure (some assessments use this)
  const data = assessment.assessmentData || assessment;

  // Must have scores and results
  return !!(data.scores && data.results);
}

/**
 * Merges latest assessment data with history data for a specific assessment type
 */
export function mergeAssessmentData(
  assessmentType: AssessmentType,
  latestData: AssessmentRecord | null,
  historyData: AssessmentRecord[]
): MergedAssessmentData {
  const config = ASSESSMENT_TYPE_CONFIG[assessmentType];
  let mergedLatest: AssessmentRecord | null = latestData;
  let mergedHistory = [...historyData];

  // Apply type-specific merge strategies
  switch (config.mergeStrategy) {
    case 'combine_subtypes':
      // Spiritual assessments: combine FASDL and FACIT-Sp-12
      mergedHistory = mergeSubtypes(mergedHistory, config.subTypes);
      break;

    case 'by_category':
      // Assessments with multiple categories: ensure latest is included in history
      if (latestData && !isInHistory(latestData, mergedHistory)) {
        mergedHistory.unshift(latestData);
        mergedHistory.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      }
      break;

    case 'expand_categories':
      // Physical assessments: expand categories into separate history entries
      mergedHistory = expandPhysicalCategories(mergedHistory);
      break;

    case 'standard':
    default:
      // Standard merge: ensure latest is in history
      if (latestData && !isInHistory(latestData, mergedHistory)) {
        mergedHistory.unshift(latestData);
        mergedHistory.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      }
      break;
  }

  // Determine completion status
  const isCompleted = isAssessmentComplete(mergedLatest);

  // Find last updated timestamp
  const allRecords = mergedLatest ? [mergedLatest, ...mergedHistory] : mergedHistory;
  const lastUpdated = allRecords.length > 0
    ? allRecords.reduce((latest, current) =>
        new Date(current.createdAt) > new Date(latest) ? current.createdAt : latest,
        allRecords[0].createdAt
      )
    : null;

  return {
    latest: mergedLatest,
    history: mergedHistory,
    isCompleted,
    lastUpdated
  };
}

/**
 * Checks if a record already exists in history (by ID or content)
 */
function isInHistory(record: AssessmentRecord, history: AssessmentRecord[]): boolean {
  return history.some(h =>
    h.id === record.id ||
    (h.createdAt === record.createdAt && h.updatedAt === record.updatedAt)
  );
}

/**
 * Merges spiritual assessment subtypes (FASDL and FACIT-Sp-12)
 */
function mergeSubtypes(history: AssessmentRecord[], subTypes: readonly string[]): AssessmentRecord[] {
  // For spiritual assessments, history endpoint returns combined data
  // Just ensure proper sorting
  return history.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
}

/**
 * Expands physical assessment categories into separate history entries
 */
function expandPhysicalCategories(history: AssessmentRecord[]): AssessmentRecord[] {
  const expanded: AssessmentRecord[] = [];

  history.forEach(assessment => {
    const categories = assessment.scores?.categories || assessment.assessmentData?.scores?.categories || [];

    if (categories.length > 0) {
      // Group by date and category to avoid duplicates
      const categoryMap = new Map<string, AssessmentRecord>();

      categories.forEach((category: any) => {
        const categoryKey = category.name.replace(/\s+/g, '_').toLowerCase();
        const assessmentDate = new Date(assessment.createdAt).toDateString();
        const mapKey = `${assessmentDate}_${categoryKey}`;

        const existing = categoryMap.get(mapKey);
        if (!existing || new Date(assessment.createdAt) > new Date(existing.createdAt)) {
          const recordId = `${assessment._id || assessment.id || `temp_${assessment.createdAt}`}_${categoryKey}`;
          categoryMap.set(mapKey, {
            ...assessment,
            _categoryName: category.name,
            _categoryScore: category.score,
            _categoryDescription: category.description,
            id: recordId
          });
        }
      });

      expanded.push(...Array.from(categoryMap.values()));
    } else {
      expanded.push(assessment);
    }
  });

  return expanded.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
}

/**
 * Validates merged data for consistency
 */
export function validateMergedData(data: MergedAssessmentData): { valid: boolean; issues: string[] } {
  const issues: string[] = [];

  // Check if latest assessment is in history (except for category-expanded types)
  if (data.latest && data.history.length > 0) {
    const latestInHistory = isInHistory(data.latest, data.history);
    if (!latestInHistory) {
      issues.push('Latest assessment not found in history');
    }
  }

  // Check completion status consistency
  const actualCompleted = isAssessmentComplete(data.latest);
  if (actualCompleted !== data.isCompleted) {
    issues.push('Completion status mismatch');
  }

  // Check history is sorted by date (newest first)
  for (let i = 1; i < data.history.length; i++) {
    if (new Date(data.history[i-1].createdAt) < new Date(data.history[i].createdAt)) {
      issues.push('History not properly sorted by date');
      break;
    }
  }

  return {
    valid: issues.length === 0,
    issues
  };
}

/**
 * Cache key generator for assessment data
 */
export function getCacheKey(userId?: string): string {
  return userId ? `lifepsensor_insights_cache_${userId}` : 'lifepsensor_insights_cache';
}

/**
 * Loads assessment data from cache
 */
export function loadFromCache(cacheKey: string): Record<AssessmentType, MergedAssessmentData> | null {
  try {
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return null;

    const parsed = JSON.parse(cached);

    // Validate cache structure
    const requiredKeys = Object.keys(ASSESSMENT_TYPE_CONFIG);
    const hasAllKeys = requiredKeys.every(key => key in parsed);

    if (!hasAllKeys) {

      localStorage.removeItem(cacheKey);
      return null;
    }

    return parsed;
  } catch (error) {

    localStorage.removeItem(cacheKey);
    return null;
  }
}

/**
 * Saves assessment data to cache
 */
export function saveToCache(cacheKey: string, data: Record<AssessmentType, MergedAssessmentData>): void {
  try {
    localStorage.setItem(cacheKey, JSON.stringify(data));
  } catch (error) {

  }
}

/**
 * Clears assessment cache
 */
export function clearCache(cacheKey: string): void {
  localStorage.removeItem(cacheKey);
}

